import { typographyCss } from '@creditornot/cb-ingredients';
import { LegacyPopover } from '@creditornot/cb-popover';
import { Tag, TagProps } from '@creditornot/cb-tag';
import { DateTime } from 'luxon';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { LocalisedValue } from 'modules/shared-menu/types';

import { PurchasabilityErrorList } from './PurchasabilityErrorList';
import { Offer } from '../../types';
import { checkIfItemIsAvailable } from '../../utils';

export type OptionBindingPurchasabilityValidationError =
  | 'all_option_values_disabled'
  | 'too_many_disabled_option_values'
  | 'binding_missing_multi_choice_config';

type PurchasabilityError = {
  optionName: LocalisedValue[];
  error: OptionBindingPurchasabilityValidationError;
};

type Props = {
  enabledConfig: Offer['enabled'];
  className?: string;
  soldOut: boolean;
  primaryLanguage: Language;
  purchasabilityErrors: PurchasabilityError[];
  special: boolean;
  isTiny: boolean;
};

const Root = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const TagContainer = styled.div`
  &:not(:last-child) {
    margin-inline-end: 12px;
  }
`;

const PopoverContent = styled.div`
  max-width: 325px;
  ${typographyCss.Body3()}
`;

const ActivePeriodTooltip = styled.div`
  max-width: 350px;
  ${typographyCss.Body3()}
`;

export const ActivePeriodInfo = ({ start, end }: { start: DateTime | null; end: DateTime | null }) => {
  const formattedStartDate = start?.toLocaleString(DateTime.DATETIME_SHORT);
  const formattedEndDate = end?.toLocaleString(DateTime.DATETIME_SHORT);

  return (
    <ActivePeriodTooltip>
      {formattedStartDate && !formattedEndDate && (
        <FormattedMessage id="special.active-from" values={{ date: formattedStartDate }} />
      )}
      {!formattedStartDate && formattedEndDate && (
        <FormattedMessage id="special.active-until" values={{ date: formattedEndDate }} />
      )}
      {formattedStartDate && formattedEndDate && (
        <FormattedMessage
          id="special.active-from-until"
          values={{ startDate: formattedStartDate, endDate: formattedEndDate }}
        />
      )}
      {!formattedStartDate && !formattedEndDate && <FormattedMessage id="special.active-for-now" />}
    </ActivePeriodTooltip>
  );
};

const OfferTag = ({ isTiny, children, ...tagProps }: TagProps & { isTiny: boolean }) => (
  <Tag {...tagProps} size={isTiny ? 'tiny' : 'small'} uppercase={!isTiny}>
    {children}
  </Tag>
);

export const offerHasTags = (
  soldOut: boolean,
  disabled: boolean,
  hasPurchasabilityErrors: boolean,
  isWoltSpecial: boolean,
  isItemAvailable: boolean,
) => {
  if (isWoltSpecial || soldOut || disabled || hasPurchasabilityErrors) {
    return true;
  }
  if (!isWoltSpecial && !isItemAvailable) {
    return true;
  }
  return false;
};

const OfferTags = ({
  className,
  enabledConfig,
  primaryLanguage,
  soldOut,
  purchasabilityErrors,
  special,
  isTiny,
}: Props) => {
  const isAvailable = checkIfItemIsAvailable(enabledConfig);

  if (!offerHasTags(soldOut, !enabledConfig.enabled, purchasabilityErrors.length > 0, special, isAvailable)) {
    return null;
  }

  return (
    <Root className={className}>
      {special && (
        <TagContainer>
          <LegacyPopover
            content={
              <ActivePeriodInfo
                start={enabledConfig.after ? DateTime.fromISO(enabledConfig.after) : null}
                end={enabledConfig.before ? DateTime.fromISO(enabledConfig.before) : null}
              />
            }
            placement="top"
            renderArrow={false}
          >
            <OfferTag
              variant={isAvailable ? 'lightBlue' : 'lightBlack'}
              isTiny={isTiny}
              onClick={e => e.preventDefault()}
            >
              <FormattedMessage id={isAvailable ? 'special.active-tag-label' : 'special.inactive-tag-label'} />
            </OfferTag>
          </LegacyPopover>
        </TagContainer>
      )}
      {!special && !isAvailable && (
        <TagContainer>
          <LegacyPopover
            content={
              <ActivePeriodInfo
                start={enabledConfig.after ? DateTime.fromISO(enabledConfig.after) : null}
                end={enabledConfig.before ? DateTime.fromISO(enabledConfig.before) : null}
              />
            }
            placement="top"
            renderArrow={false}
          >
            <OfferTag variant={'lightBlack'} isTiny={isTiny} onClick={e => e.preventDefault()}>
              <FormattedMessage id={'menu-item.not-available-tag-label'} />
            </OfferTag>
          </LegacyPopover>
        </TagContainer>
      )}
      {!enabledConfig.enabled && (
        <TagContainer>
          <OfferTag variant="lightRed" isTiny={isTiny}>
            <FormattedMessage id="state.disabled" />
          </OfferTag>
        </TagContainer>
      )}
      {soldOut && (
        <TagContainer>
          <OfferTag variant="lightOrange" isTiny={isTiny}>
            <FormattedMessage id="inventory-management.sold-out" />
          </OfferTag>
        </TagContainer>
      )}
      {purchasabilityErrors.length > 0 && (
        <TagContainer>
          <LegacyPopover
            content={
              <PopoverContent>
                <PurchasabilityErrorList
                  primaryLanguage={primaryLanguage}
                  purchasabilityValidationErrors={purchasabilityErrors}
                />
              </PopoverContent>
            }
            placement="top"
            renderArrow={false}
          >
            <OfferTag variant="lightRed" isTiny={isTiny}>
              <FormattedMessage id="menu-item.cannotBeOrdered" />
            </OfferTag>
          </LegacyPopover>
        </TagContainer>
      )}
    </Root>
  );
};

export default React.memo(OfferTags);
